// Adapted from Figma's masonry grid in web/packages/ui-platform/src/masonry_grid.tsx.

import { spacer } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import { useMemo } from 'react';
import type { StyleXStyles } from '@stylexjs/stylex';
import type { ReactNode } from 'react';

export interface MasonryGridProps<T> {
  items: T[];
  /** The index is local to the item's column. Use item identity for React keys. */
  renderItem: (item: T, index: number) => ReactNode;
  /** Must be a positive integer. */
  numColumns?: number;
  getItemHeight?: (item: T) => number;
  containerStyles?: StyleXStyles;
  columnStyles?: StyleXStyles;
  overrideGridColumnStyleWithStandardWidth?: number;
  dataTestId?: string;
}

const DEFAULT_NUM_COLUMNS = 3;
const MASONRY_GRID_GAP = spacer.spacer25;
const DEFAULT_ITEM_HEIGHT = 1;

export function MasonryGrid<T>({
  items,
  renderItem,
  numColumns = DEFAULT_NUM_COLUMNS,
  getItemHeight = () => DEFAULT_ITEM_HEIGHT,
  containerStyles: containerStyleXStyle,
  columnStyles: columnStyleXStyle,
  overrideGridColumnStyleWithStandardWidth,
  dataTestId,
}: MasonryGridProps<T>): ReactNode {
  const columns = useMemo(() => {
    const packedColumns: T[][] = Array.from({ length: numColumns }, () => []);
    const columnHeights = Array.from({ length: numColumns }, () => 0);

    items.forEach((item) => {
      let shortestColumnIndex = 0;
      let shortestColumnHeight = Infinity;

      for (let i = 0; i < numColumns; i++) {
        if (columnHeights[i] < shortestColumnHeight) {
          shortestColumnHeight = columnHeights[i];
          shortestColumnIndex = i;
        }
      }

      const currentColumn = packedColumns[shortestColumnIndex] ?? [];
      packedColumns[shortestColumnIndex] = [...currentColumn, item];
      columnHeights[shortestColumnIndex] += getItemHeight(item);
    });

    return packedColumns;
  }, [getItemHeight, items, numColumns]);

  return (
    <div
      data-testid={dataTestId}
      {...stylex.props(
        styles.masonryGridBase,
        containerStyleXStyle,
        styles.setGridTemplateColumns(overrideGridColumnStyleWithStandardWidth || 0, numColumns),
      )}
    >
      {columns.map((columnItems, columnIndex) => (
        <div key={columnIndex} {...stylex.props(styles.masonryColumn, columnStyleXStyle)}>
          {columnItems.map((item, index) => renderItem(item, index))}
        </div>
      ))}
    </div>
  );
}

/** Returns a bounded aspect-ratio weight so extreme dimensions do not dominate packing. */
export function getNormalizedItemHeight(
  dimensions?: { width: number; height: number },
  minAspectRatio: number = 0.5,
): number {
  if (!dimensions) return 1;
  const aspectRatio = dimensions.height / dimensions.width;
  return Math.min(Math.max(aspectRatio, minAspectRatio), 1 / minAspectRatio);
}

const styles = stylex.create({
  masonryGridBase: {
    display: 'grid',
    gap: MASONRY_GRID_GAP,
    maxWidth: '100%',
  },
  masonryColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: MASONRY_GRID_GAP,
  },
  setGridTemplateColumns: (overrideGridColumnStyleWithStandardWidth: number, numColumns: number) => ({
    gridTemplateColumns: overrideGridColumnStyleWithStandardWidth
      ? `repeat(${numColumns}, minmax(${overrideGridColumnStyleWithStandardWidth}px, ${overrideGridColumnStyleWithStandardWidth}px))`
      : `repeat(${numColumns}, minmax(0, 1fr))`,
  }),
});
